/* import __COLOCATED_TEMPLATE__ from './attribute-summaries.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import type Range from 'embercom/models/reporting/range';
import { type SummaryTab } from './conversation-explore-panel';
import { use } from 'ember-resources/util/function-resource';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { type ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import type { ChartSeriesDefinition } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import { action } from '@ember/object';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { inject as service } from '@ember/service';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import { TrackedMap } from 'tracked-built-ins';

interface Signature {
  Args: {
    selectedJob: Job;
    dateRange: Range;
    onShowConversationExplorer: (
      selectedConversationId: number | null,
      filters: LogicalFilter,
      attributeValuesToExplore: string[],
      summaryType: SummaryTab,
      summaryText: string,
      summaryHeading?: string,
      conversationIdsToLoad?: number[],
    ) => void;
    reportState: ReportState;
  };
}

export default class AttributeSummaries extends Component<Signature> {
  @service declare contentSuggestionsService: ContentSuggestionsService;

  @tracked suggestionCount = 0;
  @tracked topSubtopics: { name: string; count: number }[] = [];
  @tracked subtopicSuggestions: TrackedMap<string, ContentWrapper[]> = new TrackedMap();

  @use metricResource = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  constructor(owner: any, args: any) {
    super(owner, args);
    taskFor(this.loadAllSuggestions).perform();
  }

  @restartableTask
  *loadAllSuggestions(): TaskGenerator<{ name: string; count: number }[]> {
    try {
      yield this.contentSuggestionsService.fetchTopicsSummary();

      let subtopicsWithSuggestions: { name: string; count: number }[] = [];

      for (let topicDetails of this.contentSuggestionsService.topicsSummary.values()) {
        for (let subtopic of topicDetails.subtopics) {
          subtopicsWithSuggestions.push({
            name: subtopic.name,
            count: subtopic.count,
          });
        }
      }

      subtopicsWithSuggestions.sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        // If there's a tie for suggestion count, sort by conversation count
        return (
          this.getConversationCountForSubtopic(b.name) -
          this.getConversationCountForSubtopic(a.name)
        );
      });

      this.suggestionCount = subtopicsWithSuggestions.reduce(
        (sum, subtopic) => sum + subtopic.count,
        0,
      );
      this.topSubtopics = subtopicsWithSuggestions;

      yield this.loadSuggestionsForAllSubtopics(subtopicsWithSuggestions);

      return subtopicsWithSuggestions;
    } catch (error) {
      console.error('Error loading suggestions:', error);
      return [];
    }
  }

  get isLoading() {
    return this.metricResource.isLoading;
  }

  get chartDefinition() {
    return {
      view_by_other: false,
      view_by_display_limit: 5,
      view_by: this.args.selectedJob.breakdownByAttributeId,
      chart_series: this.args.selectedJob.chartSeries.map((series: ChartSeriesDefinition) => ({
        ...series,
        filters: this.args.selectedJob.filters,
      })),
    };
  }

  get breakdownAttributeValues() {
    return Object.keys(this.metricResource.flatCombinedData);
  }

  get breakdownAttributeObjects(): { name: string }[] {
    return this.breakdownAttributeValues.map((name) => ({ name }));
  }

  get selectedSubtopicsWithSuggestions(): { name: string }[] {
    return this.topSubtopics.slice(0, 10);
  }

  get selectedSubtopicsWithoutSuggestions(): { name: string }[] {
    if (!this.metricResource.flatCombinedData) {
      return [];
    }

    if (this.canShowSuggestions) {
      let subtopicsWithSuggestionNames = new Set(this.topSubtopics.map((s) => s.name));
      return this.breakdownAttributeObjects
        .filter((item) => !subtopicsWithSuggestionNames.has(item.name))
        .slice(0, Math.max(0, 10 - this.topSubtopics.length));
    } else {
      return this.breakdownAttributeObjects.slice(0, 10);
    }
  }

  get hasSubtopicsWithoutSuggestions(): boolean {
    return this.selectedSubtopicsWithoutSuggestions.length > 0;
  }

  get canShowSuggestions(): boolean {
    return this.suggestionCount > 0 && this.args.selectedJob.id === 'contentGaps';
  }

  @action
  getConversationCountForSubtopic(breakdownAttributeValue: string): number {
    if (this.args.selectedJob.breakdownByAttributeId) {
      if (this.metricResource.isLoading || !this.metricResource.flatCombinedData) {
        return 0;
      }

      let data = this.metricResource.flatCombinedData[breakdownAttributeValue];
      if (!data || !data.length) {
        return 0;
      }

      return data[0] as number;
    }
    return 0;
  }

  private findTopicForSubtopic(subtopicName: string): string | null {
    for (let [topicName, topicDetails] of this.contentSuggestionsService.topicsSummary.entries()) {
      if (topicDetails.subtopics.some((subtopic) => subtopic.name === subtopicName)) {
        return topicName;
      }
    }
    return null;
  }

  @action
  async loadSuggestionsForAllSubtopics(
    subtopics: { name: string; count: number }[],
  ): Promise<void> {
    this.subtopicSuggestions.clear();

    let subtopicsWithSuggestions = subtopics.filter((subtopic) => subtopic.count > 0);

    for (let subtopic of subtopicsWithSuggestions) {
      try {
        let suggestions = await this.loadSingleSubtopicSuggestions(subtopic.name);
        this.subtopicSuggestions.set(subtopic.name, suggestions);
      } catch (error) {
        console.error('Error loading suggestions for subtopic:', subtopic.name, error);
        this.subtopicSuggestions.set(subtopic.name, []);
      }
    }
  }

  private async loadSingleSubtopicSuggestions(subtopicName: string): Promise<ContentWrapper[]> {
    try {
      let topicName = this.findTopicForSubtopic(subtopicName);

      if (!topicName) {
        return [];
      }

      await taskFor(this.contentSuggestionsService.loadSuggestionsForTopic).perform(
        topicName,
        subtopicName,
      );

      return [...this.contentSuggestionsService.suggestionsForTopic];
    } catch (error) {
      console.error('Error loading suggestions for subtopic:', error);
      return [];
    }
  }

  @action
  getSuggestionsForSubtopicName(subtopicName: string): ContentWrapper[] {
    return this.subtopicSuggestions.get(subtopicName) || [];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::AttributeSummaries': typeof AttributeSummaries;
    'reporting/automation/ai-insights/optimize/attribute-summaries': typeof AttributeSummaries;
  }
}
